<script>
import baseMixin from '@/mixins/baseMixin'
import timesheetMixin from '@/mixins/timesheetMixin'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import PromiseDialogDto from '@/models/dialog/promiseDialogDto'
import PromiseDialogActionsDto from '@/models/dialog/promiseDialogActionsDto'
import PromiseDialogEvent from '@/constants/dialog/PromiseDialogEvent'
import VuetifyColourClass from '@/constants/core/VuetifyColourClass'
import { TimesheetChangeType } from '@/constants/disputes/TimesheetChangeType'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import PromiseDialog from '../general/promise-dialog.vue'

export default {
  name: 'DisputeResolutionConfirmationDialog',
  components: { PromiseDialog },
  mixins: [baseMixin, timesheetMixin],
  computed: {
    isAccept() {
      return this.isAcceptOperation(this.resolveStatus)
    },
    isCancel() {
      return this.isCancelOperation(this.resolveStatus)
    },
    isModified() {
      return this.isModifiedOperation(this.resolveStatus)
    },
    isTimeEntryResolve() {
      return this.resolveStatus === ResolveOperations.TIME_ENTRY
    },
    modifiedTimesheetPayload() {
      return this.retrieveModifiedTimesheetPayload(this.resolveStatus)
    },
    selectionText() {
      switch (this.resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return this.$t('dispute.confirmation.candidateTitle', [
            this.activeTimesheet.candidate.name,
          ])
        case ResolveOperations.CLIENT:
          return this.$t('dispute.confirmation.clientTitle', [
            this.activeTimesheet.clientTimesheet.details.contactName,
          ])
        case ResolveOperations.TIME_ENTRY:
          return this.$t('dispute.confirmation.timeEntryTitle', [
            this.activeTimesheet.candidate.name,
          ])
        case ResolveOperations.ORIGINAL:
        default:
          return this.$t('dispute.confirmation.originalTitle')
      }
    },
    changesList() {
      if (this.isTimeEntryResolve) return this.compileModificationsList()
      if (this.isAccept) return [this.$t('dispute.confirmation.changes.accept')]
      if (this.isCancel) return [this.$t('dispute.confirmation.changes.cancel')]

      return this.compileModificationsList()
    },
  },
  watch: {
    resolveStatus: {
      async handler(val, oldVal) {
        if (val < ResolveOperations.CANDIDATE) return
        await this.confirmDisputeResolution()
      },
    },
  },
  methods: {
    async confirmDisputeResolution() {
      const dialogConfig = new PromiseDialogDto({
        actions: [
          new PromiseDialogActionsDto({
            text: this.$i18n.t('general.cancel'),
            event: PromiseDialogEvent.Cancel,
            attributes: {
              'data-testid': 'promise-dialog-cancel-btn',
              depressed: true,
              class: 'px-8 my-2',
              color: `${VuetifyColourClass.grey} ${
                this.isDarkMode ? 'darken-5' : 'lighten-4'
              }`,
            },
          }),
          new PromiseDialogActionsDto({
            text: this.$i18n.t('general.accept'),
            event: PromiseDialogEvent.OK,
            attributes: {
              'data-testid': 'promise-dialog-ok-btn',
              color: VuetifyColourClass.primary,
              depressed: true,
              class: 'px-8 my-2',
            },
          }),
        ],
        persistent: true,
      })

      const result = await this.$refs.disputeConfirmationDialog.show(
        dialogConfig
      )

      switch (result) {
        case PromiseDialogEvent.OK:
          await this.submitResolution()
          break
        default:
          this.resetResolveStatus()
          break
      }
    },
    async submitResolution() {
      await this.submitActiveTimesheet()
      this.$emit('complete')

      await this.$store.dispatch('disputes/goToTaskForm')
    },
    compileModificationsList() {
      const modifiedText = []
      let isPositiveDifference = false
      let differenceInMins = 0

      for (const change of this.modifiedTimesheetPayload.changes) {
        isPositiveDifference = change.differenceInMinutes >= 0
        differenceInMins = Math.abs(change.differenceInMinutes)

        switch (change.type) {
          case TimesheetChangeType.delta: {
            modifiedText.push(
              this.$t(
                isPositiveDifference
                  ? 'dispute.confirmation.changes.positiveDelta'
                  : 'dispute.confirmation.changes.negativeDelta',
                [differenceInMins]
              )
            )

            break
          }
          case TimesheetChangeType.shift: {
            modifiedText.push(
              this.$t(
                isPositiveDifference
                  ? 'dispute.confirmation.changes.positiveShift'
                  : 'dispute.confirmation.changes.negativeShift',
                [differenceInMins]
              )
            )

            break
          }
          case TimesheetChangeType.unpaidBreaks: {
            modifiedText.push(
              this.$t(
                isPositiveDifference
                  ? 'dispute.confirmation.changes.positiveBreaks'
                  : 'dispute.confirmation.changes.negativeBreaks',
                [differenceInMins]
              )
            )

            break
          }
        }
      }

      if (isNullOrEmptyArray(modifiedText))
        return [this.$t('dispute.confirmation.changes.accept')]

      return modifiedText
    },
  },
}
</script>

<template>
  <PromiseDialog ref="disputeConfirmationDialog">
    <template v-slot:title>
      <v-card-title class="font-weight-medium">
        {{ $t('dispute.confirmation.modalTitle') }}
      </v-card-title>
      <v-divider />
    </template>

    <template v-slot:text>
      <v-card-text class="text-left mt-4 ws-pre-wrap">
        <p class="mb-0 text-subtitle-1 font-weight-medium">{{
          selectionText
        }}</p>

        <ul>
          <li v-for="(change, index) of changesList" :key="index">
            {{ change }}
          </li>
        </ul>
      </v-card-text>
    </template>
  </PromiseDialog>
</template>
